import React from 'react';
import { useSettings } from '../contexts/SettingsContext';

const variants = {
  primary: 'text-white focus:ring-[var(--color-primary)]',
  secondary: 'text-white focus:ring-[var(--color-secondary)]',
  outline: 'border-2 bg-transparent focus:ring-[var(--color-primary)]',
  text: 'bg-transparent focus:ring-[var(--color-primary)]',
};

const sizes = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-5 py-2.5 text-base',
  lg: 'px-6 py-3 text-base',
};

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  className = '',
  type = 'button',
  onClick,
  ...props
}) => {
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';

  const variantStyles = {
    primary: {
      backgroundColor: primaryColor,
      borderColor: primaryColor,
      color: '#fff',
    },
    secondary: {
      backgroundColor: secondaryColor,
      borderColor: secondaryColor,
      color: '#fff',
    },
    outline: {
      backgroundColor: 'transparent',
      borderColor: primaryColor,
      color: primaryColor,
    },
    text: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      color: primaryColor,
    },
  };

  const style = variantStyles[variant] || variantStyles.primary;
  
  // Apply fogged (lighter opacity) primary color when disabled
  const isDisabledState = disabled || loading;
  const buttonStyle = {
    backgroundColor: style.backgroundColor,
    borderColor: style.borderColor,
    color: style.color,
    opacity: isDisabledState ? 0.5 : 1,
  };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={`
        inline-flex items-center justify-center font-medium rounded-lg
        transition-all duration-250 ease-out
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900
        disabled:cursor-not-allowed
        hover:opacity-95 hover:shadow-md
        ${variants[variant] || variants.primary}
        ${sizes[size] || sizes.md}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      style={buttonStyle}
      {...props}
    >
      {loading ? (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : null}
      {children}
    </button>
  );
};

export default Button;
